/**
 * Mesa do perito na modalidade assinatura eletrônica (E74.F3, ADR-0075).
 *
 * A etapa "Mesa" do caso eletrônico. Na modalidade grafotécnica ela é o canvas
 * de comparação de traço; aqui o perito **não compara traço**: ele lê o quadro
 * que o motor determinístico produziu na análise preliminar, decide o grau
 * (podendo reclassificar o apurado, com justificativa) e escreve a conclusão
 * que vai para o laudo.
 *
 * O quadro é o MESMO componente da pré-análise, em leitura: repetir o desenho
 * numa segunda cópia faria as duas telas divergirem na primeira mudança da
 * régua, e é o mesmo dado.
 *
 * E78.F4 — a mesa deixou de ser uma pilha de N cópias do quadro inteiro. Com as
 * cinco peças do caso real o perito rolava cinco telas para achar as duas que
 * divergiram, e nada dizia o que estava sendo comparado nem quanto faltava para
 * o laudo. Agora: painel do caso no topo (o confronto, a tabela das peças e o
 * contador), o quadro por eixos da peça SELECIONADA e a decisão numa coluna
 * lateral fixa. A navegação é pela tabela, e não há ação em lote — cada
 * conclusão continua um ato do perito (decisões do owner, 2026-09-01).
 *
 * Guard de mutação em voo (mesma doutrina do formulário da F2): enquanto uma
 * conclusão ou reabertura está em curso, os controles daquela CCB ficam
 * travados — a resposta de um POST antigo não pode sobrescrever o estado que um
 * POST novo acabou de produzir.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CaseStageStepper } from '@/components/Casos/CaseStageStepper';
import { ColunaDeDecisao } from '@/components/ExameEletronico/ColunaDeDecisao';
import type { DecisaoAcao } from '@/components/ExameEletronico/DecisaoDoPerito';
import { PainelDoCaso } from '@/components/ExameEletronico/PainelDoCaso';
import { QuadroPorEixos } from '@/components/ExameEletronico/QuadroPorEixos';
import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { MesaEletronicaEstado } from '@/pages/MesaEletronicaStates';
import { useApiClient } from '@/lib/api';
import { getCase, type CaseRecordData } from '@/lib/cases';
import {
  concluirExame,
  ExameEletronicoError,
  listExamesEletronicos,
  reabrirExame,
  type ConclusaoEletronicaInput,
  type ExameEletronicoCcbData,
  type ExameEletronicoErrorReason,
  type ProtocoloAssinaturaCasoData,
} from '@/lib/exame_eletronico';
import { tituloDaPeca } from '@/lib/exame_eletronico_peca';

const t = ptBR.mesaEletronica;
const te = ptBR.exameEletronico;
const ti = ptBR.exameEletronico.importarProtocolo;

const _ERROR_MESSAGE: Record<ExameEletronicoErrorReason, string> = {
  case_not_found: te.errorCaseNotFound,
  document_not_found: te.errorDocumentNotFound,
  not_found: te.errorNotFound,
  modalidade_invalida: te.errorModalidadeInvalida,
  sem_protocolo: te.errorSemProtocolo,
  nao_verificado: te.errorNaoVerificado,
  justificativa_requerida: te.errorJustificativaRequerida,
  concluido: te.errorConcluido,
  nao_concluido: te.errorNaoConcluido,
  certificacao_requerida: te.errorCertificacaoRequerida,
  protocolo_peca_concluida: te.errorProtocoloPecaConcluida,
  protocolo_nao_importado: ti.erros.protocolo_nao_importado,
  documento_ilegivel: ti.erros.documento_ilegivel,
  documento_sem_texto: ti.erros.documento_sem_texto,
  arquivo_nao_suportado: ti.erros.arquivo_nao_suportado,
  arquivo_vazio: ti.erros.arquivo_vazio,
  arquivo_muito_grande: ti.erros.arquivo_muito_grande,
  // A mesa não anexa foto nem confere rosto; as razões existem no contrato da
  // raia e o mapa é exaustivo, então elas caem na frase genérica em vez de
  // sumir do tipo.
  arquivo_nao_e_imagem: te.errorGeneric,
  foto_integridade: te.errorGeneric,
  foto_indisponivel: te.errorGeneric,
  conferencia_sem_par: te.errorGeneric,
  conferencia_indisponivel: te.errorGeneric,
  provider_auth: te.errorGeneric,
  provider_credit: te.errorGeneric,
  sem_chave: te.errorGeneric,
  validation: te.errorValidation,
  forbidden: te.errorForbidden,
  network: te.errorNetwork,
  unknown: te.errorGeneric,
};

function messageFor(err: unknown): string {
  if (err instanceof ExameEletronicoError) return _ERROR_MESSAGE[err.reason];
  return te.errorGeneric;
}

type PorCcb<T> = Readonly<Record<string, T>>;

function semChave<T>(mapa: PorCcb<T>, chave: string): PorCcb<T> {
  const { [chave]: descartado, ...resto } = mapa;
  void descartado;
  return resto;
}

/**
 * Estado da mesa: carga do caso + CCBs e as duas mutações por CCB.
 *
 * Extraído da página pelo mesmo motivo de `useWorkspaceData`: a página fica
 * sendo layout, e a regra de "quem está em voo, o que deu erro, o que deu
 * certo" — que é o guard de mutação — tem um lugar só.
 */
function useMesaEletronica(caseId: string) {
  const client = useApiClient();
  const [caso, setCaso] = useState<CaseRecordData | null>(null);
  const [protocolo, setProtocolo] = useState<ProtocoloAssinaturaCasoData | null>(null);
  const [ccbs, setCcbs] = useState<ExameEletronicoCcbData[] | null>(null);
  const [erroGlobal, setErroGlobal] = useState<string | null>(null);
  const [acaoPorCcb, setAcaoPorCcb] = useState<PorCcb<DecisaoAcao>>({});
  const [erroPorCcb, setErroPorCcb] = useState<PorCcb<string>>({});
  const [sucessoPorCcb, setSucessoPorCcb] = useState<PorCcb<string>>({});

  const carregar = useCallback(async (): Promise<void> => {
    const [registro, lista] = await Promise.all([
      getCase(client, caseId),
      listExamesEletronicos(client, caseId),
    ]);
    setCaso(registro);
    setProtocolo(lista.protocolo);
    setCcbs(lista.pecas);
  }, [client, caseId]);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const [registro, lista] = await Promise.all([
          getCase(client, caseId),
          listExamesEletronicos(client, caseId),
        ]);
        if (cancelado) return;
        setCaso(registro);
        setProtocolo(lista.protocolo);
        setCcbs(lista.pecas);
      } catch (err) {
        if (!cancelado) setErroGlobal(messageFor(err));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [client, caseId]);

  const executar = useCallback(
    async (
      docId: string,
      acao: Exclude<DecisaoAcao, null>,
      operacao: () => Promise<unknown>,
      sucesso: string,
    ): Promise<void> => {
      setAcaoPorCcb((atual) => ({ ...atual, [docId]: acao }));
      setErroPorCcb((atual) => semChave(atual, docId));
      setSucessoPorCcb((atual) => semChave(atual, docId));
      try {
        await operacao();
        // Relê a lista inteira em vez de costurar a resposta no estado local: a
        // conclusão da última CCB muda o STATUS DO CASO, e o stepper leria um
        // valor velho se só a linha da CCB fosse atualizada.
        await carregar();
        setSucessoPorCcb((atual) => ({ ...atual, [docId]: sucesso }));
      } catch (err) {
        setErroPorCcb((atual) => ({ ...atual, [docId]: messageFor(err) }));
      } finally {
        setAcaoPorCcb((atual) => semChave(atual, docId));
      }
    },
    [carregar],
  );

  const concluir = useCallback(
    (docId: string, conclusao: ConclusaoEletronicaInput): void => {
      void executar(
        docId,
        'concluindo',
        () => concluirExame(client, caseId, docId, conclusao),
        t.concluida,
      );
    },
    [client, caseId, executar],
  );

  const reabrir = useCallback(
    (docId: string, motivo: string): void => {
      void executar(
        docId,
        'reabrindo',
        () => reabrirExame(client, caseId, docId, motivo),
        t.reaberta,
      );
    },
    [client, caseId, executar],
  );

  const total = ccbs?.length ?? 0;
  const todasConcluidas =
    ccbs !== null && total > 0 && ccbs.every((c) => c.exame?.status === 'concluido');

  return {
    caso,
    protocolo,
    ccbs,
    total,
    todasConcluidas,
    erroGlobal,
    acaoPorCcb,
    erroPorCcb,
    sucessoPorCcb,
    concluir,
    reabrir,
  };
}

/** O quadro da peça aberta, em leitura — ou o caminho de volta à preliminar. */
function QuadroDaPeca({
  peca,
  indice,
  onAbrirPreliminar,
}: {
  peca: ExameEletronicoCcbData;
  indice: number;
  onAbrirPreliminar: () => void;
}): JSX.Element {
  const resultado = peca.exame?.resultado ?? null;
  return (
    <Blueprint as="article" className="space-y-sc-4 p-sc-6" data-testid="mesa-peca-aberta">
      <h2 className="text-display-sm text-fg">{tituloDaPeca(peca, indice)}</h2>
      {resultado === null ? (
        <div data-testid="mesa-peca-sem-exame">
          <p className="text-body-sm text-fg-muted">{t.semExame}</p>
          <Button type="button" variant="secondary" className="mt-sc-3" onClick={onAbrirPreliminar}>
            {t.irParaPreliminar}
          </Button>
        </div>
      ) : (
        <QuadroPorEixos resultado={resultado} tendencia={peca.exame?.tendencia ?? null} />
      )}
    </Blueprint>
  );
}

export function MesaEletronicaPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    caso,
    protocolo,
    ccbs,
    todasConcluidas,
    erroGlobal,
    acaoPorCcb,
    erroPorCcb,
    sucessoPorCcb,
    concluir,
    reabrir,
  } = useMesaEletronica(caseId);
  const [selecionada, setSelecionada] = useState<string | null>(null);

  const pecas = ccbs ?? [];
  const indiceAberto = Math.max(
    pecas.findIndex((p) => p.source_document_id === selecionada),
    0,
  );
  const aberta = pecas[indiceAberto];
  const docId = aberta?.source_document_id ?? '';

  return (
    <section
      className="mx-auto max-w-7xl"
      data-testid="mesa-eletronica-page"
      aria-labelledby="mesa-eletronica-title"
    >
      <h1 id="mesa-eletronica-title" className="text-display-md text-fg">
        {t.pageTitle}
      </h1>
      <p className="mt-1 max-w-prose text-body-md text-fg-muted">{t.pageSubtitle}</p>

      <div className="mt-sc-4">
        <CaseStageStepper
          caseId={caseId}
          workflowStatus={caso?.workflow_status ?? null}
          activeStage="mesa"
          modalidade="eletronica"
        />
      </div>

      <MesaEletronicaEstado erroGlobal={erroGlobal} ccbs={ccbs} />

      {aberta !== undefined ? (
        <>
          <div className="mt-sc-6">
            <PainelDoCaso
              protocolo={protocolo}
              pecas={pecas}
              selecionada={aberta.source_document_id}
              onSelecionar={setSelecionada}
            />
          </div>

          <div className="mt-sc-6 grid gap-sc-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
            <QuadroDaPeca
              key={docId}
              peca={aberta}
              indice={indiceAberto}
              onAbrirPreliminar={() => navigate(`/casos/${caseId}/pre-analise`)}
            />

            <ColunaDeDecisao
              pecas={pecas}
              peca={aberta}
              indice={indiceAberto}
              acaoEmCurso={acaoPorCcb[docId] ?? null}
              erro={erroPorCcb[docId]}
              sucesso={sucessoPorCcb[docId]}
              onConcluir={(conclusao) => concluir(docId, conclusao)}
              onReabrir={(motivo) => reabrir(docId, motivo)}
              onSelecionar={setSelecionada}
            />
          </div>
        </>
      ) : null}

      {todasConcluidas ? (
        <div className="mt-sc-8" data-testid="mesa-eletronica-concluida">
          <p className="text-body-md text-fg">{t.todasConcluidas}</p>
          <Button
            type="button"
            className="mt-sc-3"
            onClick={() => navigate(`/casos/${caseId}/laudo`)}
            data-testid="mesa-eletronica-ir-laudo"
          >
            {t.irParaLaudo}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
